import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useKey } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Modal from '@/components/base/Modal'
import ResponsiveVideo from '@/components/base/ResponsiveVideo'
import { useLenis } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData, WPVideo } from '@/types'

export type VideoSectionProps = HTMLAttributes<HTMLElement> &
  Partial<{
    media: {
      video_short_desktop: WPVideo
      video_short_mobile: WPVideo
      video_long: WPVideo
      poster: WPImage
    }
    theme: WPPageData['acf']['theme']
  }>

const VideoSection = ({ media, theme, ...props }: VideoSectionProps) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const playerRef = useRef<HTMLVideoElement>(null)
  const wrapper = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()

  useKey('Escape', () => setModalOpen(false), { event: 'keydown' }, [])

  useEffect(() => {
    if (isModalOpen) playerRef?.current?.play()
    if (!isModalOpen) playerRef?.current?.pause()
  }, [isModalOpen])

  useGSAP(
    (context) => {
      if (!lenis || !loaded) return
      const selector = context?.selector || gsap.utils.selector(wrapper)
      const el = selector('[data-mouse-follow]')

      // Create matchMedia instance
      const mm = gsap.matchMedia()

      // Only apply the mouse follow effect on screens larger than 768px
      mm.add('(min-width: 768px)', () => {
        gsap.set(el, { xPercent: -50, yPercent: -50 })

        const pos = { x: 0, y: 0 }
        const mouse = { x: 0, y: 0 }
        const speed = 0.1

        const fpms = 60 / 1000

        const xSet = gsap.quickSetter(el, 'x', 'px')
        const ySet = gsap.quickSetter(el, 'y', 'px')

        const updateMousePosition = (e: MouseEvent) => {
          if (!wrapper.current) return
          const bounds = wrapper.current.getBoundingClientRect()
          mouse.x = e.clientX - bounds.left
          mouse.y = e.clientY - bounds.top
        }

        wrapper.current.addEventListener('mousemove', updateMousePosition)

        // Store the ticker function so we can remove it properly
        const tickerFunction = (time: number, deltaTime: number) => {
          const delta = deltaTime * fpms
          const dt = 1.0 - Math.pow(1.0 - speed, delta)

          pos.x += (mouse.x - pos.x) * dt
          pos.y += (mouse.y - pos.y) * dt
          xSet(pos.x)
          ySet(pos.y)
        }

        gsap.ticker.add(tickerFunction)

        // Return a cleanup function for this specific context
        return () => {
          wrapper.current?.removeEventListener('mousemove', updateMousePosition)
          gsap.ticker.remove(tickerFunction)
        }
      })

      return () => {
        mm.revert() // Clean up all matchMedia contexts
      }
    },
    {
      dependencies: [lenis, loaded],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  return (
    <>
      <section
        ref={wrapper}
        onClick={() => {
          setModalOpen(true)
          playerRef?.current?.play()
        }}
        {...props}
        className={cn([
          'relative z-10 aspect-[1440/1000] w-full cursor-pointer overflow-hidden',
          {
            'bg-background': theme === 'light',
            'bg-black': theme === 'dark'
          }
        ])}
      >
        {media?.video_short_desktop?.url || media?.video_short_mobile?.url ? (
          <ResponsiveVideo
            video_desktop={media.video_short_desktop}
            video_mobile={media.video_short_mobile}
            poster={media.poster}
            className="absolute inset-0 z-10 h-full w-full object-cover"
          />
        ) : null}
        <div
          className={cn(
            'absolute left-1/2 top-1/2 z-10 size-[5rem] -translate-x-1/2 -translate-y-1/2',
            'md:left-0 md:top-0 md:size-[30rem] md:translate-x-0 md:translate-y-0',
            'rounded-full bg-white mix-blend-difference',
            'flex items-center justify-center'
          )}
          data-mouse-follow
        >
          <svg
            className="h-[30rem] w-[20rem]"
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.625 15L10.3125 20.3125V9.6875L20.625 15Z"
              fill="black"
            />
          </svg>
        </div>
      </section>
      <Modal open={isModalOpen}>
        <div
          className={cn([
            'fixed left-0 top-0 z-modal flex h-dvh w-full items-center justify-center bg-black object-cover',
            '[&_video]:h-full [&_video]:w-full [&_video]:object-cover',
            { 'pointer-events-auto': isModalOpen }
          ])}
        >
          <video ref={playerRef} controls data-poster={media?.poster?.url}>
            <source
              src={media?.video_long?.url}
              type={media?.video_long?.mime_type}
            />
          </video>
          <button
            className="absolute right-[2rem] top-[2rem] z-20 fill-white"
            onClick={() => setModalOpen(false)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 12 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.443359"
                y="9.74261"
                width="14"
                height="2"
                rx="1"
                transform="rotate(-45 0.443359 9.74261)"
                fill="white"
              />
              <rect
                x="10.3438"
                y="11.1569"
                width="14"
                height="2"
                rx="1"
                transform="rotate(-135 10.3438 11.1569)"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </Modal>
    </>
  )
}

export default VideoSection
